import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppContext } from "@/app/context";
import { useTranslatedFile } from "@/app/hooks";
import { validateRegistryId } from "@/app/lib/registry-helpers";
import {
  Registry,
  RegistryCode,
  useAccountLinkedRegistriesQuery,
  useLinkRegistryMutation,
  useRegistriesQuery,
} from "@/app/types/generated/graphql";

export const useRegistryForm = (selectedRegistry?: Registry | null) => {
  const { t } = useTranslation();

  const { setUserAccountInfo } = useAppContext();
  const [showLegalDocumentModal, setShowLegalDocumentModal] = useState(false);
  const { loading: loadingTranslatedFile, file } = useTranslatedFile("translation", "productHoldingTerms");

  const { data: registriesData, loading: registriesLoading } = useRegistriesQuery();
  const { data: linkedRegistriesData, loading: linkedRegistriesLoading } = useAccountLinkedRegistriesQuery({
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      setUserAccountInfo((userAccountInfo) => {
        if (!userAccountInfo) return null;

        return {
          ...userAccountInfo,
          linkedRegistries: data.account?.linkedRegistries,
        };
      });
    },
  });

  const [linkRegistry] = useLinkRegistryMutation({
    refetchQueries: ["AccountLinkedRegistries"],
  });

  const registryOptions = useMemo(
    () => registriesData?.registries?.filter((reg) => reg.code !== RegistryCode.Icx) || [],
    [registriesData?.registries],
  );

  const formatValidationText = useMemo(() => {
    const validationMessages: { [key in RegistryCode]?: string } = {
      [RegistryCode.Mrets]: t("Please enter valid MRETS registry ID"),
      [RegistryCode.Ercot]: t("Please enter valid ERCOT Account Name"),
    };

    return selectedRegistry?.code ? validationMessages[selectedRegistry.code as RegistryCode] : "";
  }, [selectedRegistry?.code, t]);

  const validateAccountNumber = useCallback(
    (value, status, setStatus) => {
      let error;
      if (!value) return;
      if (!validateRegistryId(selectedRegistry?.code, value)) {
        error = formatValidationText;
      }
      setStatus({
        ...status,
        error,
      });

      return error;
    },
    [formatValidationText, selectedRegistry?.code],
  );

  return {
    showLegalDocumentModal,
    setShowLegalDocumentModal,
    file,
    registryOptions,
    linkedRegistriesData,
    loading: registriesLoading || linkedRegistriesLoading,
    loadingTranslatedFile,
    validateAccountNumber,
    linkRegistry,
  };
};
